
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { MapPin, BookOpen, Camera, Heart, Plus, Sparkles, Compass, Star } from 'lucide-react';
import LoginPrompt from '@/components/shared/LoginPrompt';

const Dashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-bg-primary text-text-secondary">加载中...</div>;

  if (!user) {
    return (
      <div className="min-h-screen bg-bg-primary">
        <Navbar />
        <div className="pt-28 pb-20">
          <LoginPrompt title="数据中心" description="登录后查看你的旅行数据与个性化推荐" />
        </div>
      </div>
    );
  }

  const stats = [
    { label: '总行程数', value: 12, icon: MapPin, border: 'border-primary', text: 'text-primary' },
    { label: '旅行日记数', value: 8, icon: BookOpen, border: 'border-accent', text: 'text-accent' },
    { label: '打卡次数', value: 45, icon: Camera, border: 'border-success', text: 'text-success' },
    { label: '收藏数量', value: 23, icon: Heart, border: 'border-danger', text: 'text-danger' },
  ];

  const actions = [
    { label: '创建行程', to: '/planner', icon: Plus },
    { label: '写旅行日记', to: '/diary/new', icon: BookOpen },
    { label: '浏览攻略', to: '/guides', icon: Sparkles },
    { label: '探索目的地', to: '/destinations', icon: Compass },
  ];

  const activities = [
    { title: '创建了大理5日游行程', time: '2小时前', icon: MapPin },
    { title: '发布了一篇旅行日记', time: '昨天', icon: BookOpen },
    { title: '在丽江古城打卡', time: '3天前', icon: Camera },
    { title: '收藏了《厦门美食攻略》', time: '5天前', icon: Heart },
  ];

  return (
    <div className="min-h-screen bg-bg-primary">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-28 pb-20">
        <div className="bg-gradient-to-r from-primary to-primary-hover rounded-card p-8 text-white mb-8 shadow-card">
          <h1 className="text-2xl font-bold mb-2">你好，{user?.email?.split('@')[0] || '旅行者'}！</h1>
          <p className="opacity-90">旅行不仅是到达目的地，更是发现自我的过程。</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {stats.map((s) => (
            <Card key={s.label} className={`border-0 shadow-card rounded-card bg-white border-t-4 ${s.border}`}>
              <CardContent className="p-5">
                <s.icon className={`w-6 h-6 ${s.text} mb-3`} />
                <p className="text-3xl font-bold text-text-primary mb-1">{s.value}</p>
                <p className="text-sm text-text-secondary">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {actions.map((a) => (
            <Card key={a.label} className="border-0 shadow-card rounded-card bg-white hover:shadow-hover hover:-translate-y-1 transition-all cursor-pointer" onClick={() => navigate(a.to)}>
              <CardContent className="p-5 flex flex-col items-center text-center">
                <div className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center mb-3">
                  <a.icon className="w-5 h-5 text-primary" />
                </div>
                <p className="font-medium text-text-primary">{a.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <h2 className="text-xl font-bold text-text-primary mb-4">最近活动</h2>
            <div className="relative pl-6 space-y-6">
              <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-primary/20" />
              {activities.map((a, i) => (
                <div key={i} className="relative animate-fade-in" style={{ animationDelay: `${i * 100}ms` }}>
                  <div className="absolute -left-4 top-1 w-3 h-3 rounded-full bg-primary border-2 border-white shadow-sm" />
                  <Card className="border-0 shadow-card rounded-card bg-white">
                    <CardContent className="p-4 flex items-center gap-3">
                      <a.icon className="w-5 h-5 text-primary shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-text-primary">{a.title}</p>
                        <p className="text-xs text-text-tertiary">{a.time}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <Card className="border-0 shadow-card rounded-card bg-gradient-to-br from-primary to-accent text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm opacity-90">当前积分</p>
                  <Star className="w-4 h-4 opacity-80" />
                </div>
                <p className="text-4xl font-bold mb-4">1,250</p>
                <p className="text-xs opacity-80 mb-4">银牌会员</p>
                <div className="w-full bg-white/20 rounded-full h-2 mb-4">
                  <div className="bg-white rounded-full h-2" style={{ width: '60%' }} />
                </div>
                <Button className="w-full bg-white text-primary hover:bg-white/90 rounded-btn font-medium">立即签到</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

